import { useState, type SyntheticEvent } from 'react'

import { Button, Sheet } from '../../components/ui'
import { CARAVAN_TRIP } from '../../config/trip'
import type { AgendaEvent, AgendaEventInput } from '../../data/local/agendaRepository'

export function EventEditor({
  event,
  tripId,
  onClose,
  onSave,
  onDelete,
}: {
  event?: AgendaEvent | undefined
  tripId: string
  onClose: () => void
  onSave: (input: AgendaEventInput, event?: AgendaEvent) => Promise<void>
  onDelete?: ((event: AgendaEvent) => Promise<void>) | undefined
}) {
  const [date, setDate] = useState(event?.date ?? CARAVAN_TRIP.startsOn)
  const [time, setTime] = useState(event?.time ?? '09:00')
  const [title, setTitle] = useState(event?.title ?? '')
  const [place, setPlace] = useState(event?.place ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  async function submit(submitEvent: SyntheticEvent<HTMLFormElement>): Promise<void> {
    submitEvent.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await onSave({ tripId, date, time, title, place }, event)
      onClose()
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Enregistrement impossible.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(): Promise<void> {
    if (!event || !onDelete) return
    setSaving(true)
    setError(null)
    try {
      await onDelete(event)
      onClose()
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Suppression impossible.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet title={event ? 'Modifier l’événement' : 'Nouvel événement'} onClose={onClose}>
      <form className="event-form" onSubmit={(submitEvent) => void submit(submitEvent)}>
        <label htmlFor="event-date">Date</label>
        <input id="event-date" min={CARAVAN_TRIP.startsOn} max={CARAVAN_TRIP.endsOn} type="date" value={date} onChange={(change) => setDate(change.target.value)} />
        <label htmlFor="event-time">Heure</label>
        <input id="event-time" type="time" value={time} onChange={(change) => setTime(change.target.value)} />
        <label htmlFor="event-title">Titre</label>
        <input id="event-title" maxLength={100} required value={title} onChange={(change) => setTitle(change.target.value)} />
        <label htmlFor="event-place">Lieu</label>
        <input id="event-place" maxLength={120} value={place} onChange={(change) => setPlace(change.target.value)} />
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <Button disabled={saving} fullWidth type="submit">
          {saving ? 'Conservation…' : 'Conserver dans l’agenda'}
        </Button>
        {event && onDelete ? confirmingDelete ? <div className="editor-delete-confirm" role="group" aria-label="Confirmer la suppression">
          <p>Supprimer ce rendez-vous ?</p>
          <Button disabled={saving} variant="danger" type="button" onClick={() => void remove()}>Oui, supprimer</Button>
          <Button disabled={saving} variant="ghost" type="button" onClick={() => setConfirmingDelete(false)}>Annuler</Button>
        </div> : <Button variant="ghost" type="button" onClick={() => setConfirmingDelete(true)}>Supprimer ce rendez-vous</Button> : null}
      </form>
    </Sheet>
  )
}
